import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchBankStatementRowTransactions } from '../../../../store/dashboard/bankStatement/apiHelper';
import { useLatestRequestGuard } from '../../../../common/hooks/useLatestRequestGuard';
import { Mixpanel } from '../../../../utils/mixpanel';
import { AICA_BANK_STATEMENT_ROW_TRANSACTIONS_VIEWED } from '../../../../utils/constants/mixpanelEvents/investorEvents';
import type { BankTransaction } from '../Financials/components/FinancialsFromBankingV2/types';
import { buildAccountLabelById, resolveRowTransactionsAccountIds, resolveRowTransactionsWindow } from './bankRowTransactions';
import { ROW_TRANSACTIONS_ERROR_MESSAGE } from './constants';
import { AGGREGATED } from '../../../../utils/constants/commonConstants';

const AGGREGATED_SCOPE = 'Aggregated';
const ACCOUNT_SCOPE = 'Account';

interface RowTransactionsDrawerState {
    rowLabel: string;
    transactions: BankTransaction[];
    loading: boolean;
    errorMessage?: string;
}

interface UseBankRowTransactionsDrawerArgs {
    orgId: string;
    viewedAccountId: string | undefined;
    bankingOverview: { accountId: string; name?: string; accountNumber?: string | number | null }[];
    summaryData: { bankName: string; accountNumber?: string | number | null }[];
    headers: { key: string; name: string }[] | undefined;
    rangeApplied: boolean;
    applied: { startDate: string; endDate: string };
}

/** Owns the Banking tab's "View Transactions" drawer: scope resolution, fetch, and race-safe state. */
export function useBankRowTransactionsDrawer({
    orgId,
    viewedAccountId,
    bankingOverview,
    summaryData,
    headers,
    rangeApplied,
    applied,
}: UseBankRowTransactionsDrawerArgs) {
    const [drawer, setDrawer] = useState<RowTransactionsDrawerState | null>(null);
    const { startRequest, isCurrent } = useLatestRequestGuard();

    const accountIds = useMemo(
        () => resolveRowTransactionsAccountIds(viewedAccountId, bankingOverview),
        [viewedAccountId, bankingOverview],
    );
    const appliedWindow = useMemo(
        () => resolveRowTransactionsWindow(rangeApplied, applied, headers),
        [rangeApplied, applied.startDate, applied.endDate, headers],
    );
    const accountLabelById = useMemo(() => buildAccountLabelById(bankingOverview, summaryData), [bankingOverview, summaryData]);
    const canViewRowTransactions = accountIds.length > 0 && appliedWindow !== null;

    useEffect(() => {
        startRequest();
        setDrawer(null);
    }, [orgId, startRequest]);

    const openRowTransactions = useCallback(
        (rowLabel: string) => {
            if (!appliedWindow || accountIds.length === 0) return;
            Mixpanel.track(AICA_BANK_STATEMENT_ROW_TRANSACTIONS_VIEWED, {
                rowName: rowLabel,
                accountScope: viewedAccountId?.toLowerCase() === AGGREGATED ? AGGREGATED_SCOPE : ACCOUNT_SCOPE,
            });
            const token = startRequest();
            setDrawer({ rowLabel, transactions: [], loading: true });
            fetchBankStatementRowTransactions(
                orgId,
                { rowLabel, ...appliedWindow, accountIds: accountIds.join(',') },
                {
                    onSuccess: (rows) => {
                        if (!isCurrent(token)) return;
                        setDrawer({ rowLabel, transactions: rows, loading: false });
                    },
                    onError: () => {
                        if (!isCurrent(token)) return;
                        setDrawer({ rowLabel, transactions: [], loading: false, errorMessage: ROW_TRANSACTIONS_ERROR_MESSAGE });
                    },
                },
            );
        },
        [orgId, viewedAccountId, accountIds, appliedWindow, startRequest, isCurrent],
    );

    const closeRowTransactions = useCallback(() => {
        startRequest();
        setDrawer(null);
    }, [startRequest]);

    return { drawer, canViewRowTransactions, openRowTransactions, closeRowTransactions, accountLabelById };
}
